import React, { useState } from 'react';
import { useT } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import type { Message } from '@/types';

/**
 * The newest transaction id among this session's loaded rows, or null. Rows come from the staff transcript route,
 * which is the only place a row carries one; live-pushed rows have none and are skipped.
 */
export function latestTransactionId(messages: Message[], sessionId: string): string | null {
  let best: { at: string; tx: string } | null = null;
  for (const m of messages) {
    if (m.sessionId !== sessionId || !m.transactionId) continue;
    if (!best || (m.timestamp ?? '') >= best.at) best = { at: m.timestamp ?? '', tx: m.transactionId };
  }
  return best?.tx ?? null;
}

const CopyRow: React.FC<{ label: string; value: string | null; testId: string }> = ({ label, value, testId }) => {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!value) return;
    void navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  };
  return (
    <div className="ac-ctx-kv" data-testid={testId}>
      <span className="ac-ctx-kv-k">{label}</span>
      <span className="ac-ctx-kv-v" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <code dir="ltr" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value ?? undefined}>
          {value ?? t('cp_trace_none')}
        </code>
        {value && (
          <button type="button" className="ac-btn ghost sm" onClick={copy} data-testid={`${testId}-copy`}>
            {copied ? t('cp_trace_copied') : t('cp_trace_copy')}
          </button>
        )}
      </span>
    </div>
  );
};

/**
 * 2026-10-31 - the ids an admin pastes into the Logs page: the conversation's session id (every row of the conversation)
 * and the transaction id of the newest loaded row (one turn). Staff-only by construction: this is the agent console.
 */
export const TraceIds: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { t } = useT();
  const latestTx = useConversationStore((s) => latestTransactionId(s.messages, sessionId));
  return (
    <div className="ac-ctx-sec" style={{ borderBottom: 'none' }} data-testid="cp-trace-ids">
      <div className="ac-ctx-sec-title">{t('cp_trace_title')}</div>
      <CopyRow label={t('cp_trace_session')} value={sessionId} testId="cp-trace-session" />
      <CopyRow label={t('cp_trace_transaction')} value={latestTx} testId="cp-trace-transaction" />
    </div>
  );
};
